import { ReactNode } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import {
  ScaffolderFieldError,
  ScaffolderInputHeader,
} from '../../../components';
import {
  HELM_CONFIG_INFO,
  KUSTOMIZE_CONFIG_INFO,
  MANIFESTS_CONFIG_INFO,
} from '../infoDoc';
import { useStyles } from '../styles';
import {
  DEFAULT_HELM_CONFIGURATION,
  RepoType,
  SourceConfiguration,
} from '../types';
import { inferRepoType, resolveRepoType } from '../utils';
import {
  containsTemplateExpression,
  validateHelmReleaseName,
  validateSourceGitURL,
  validateSourceInputPattern,
  validateSourceRepoURL,
  validateSourceRevision,
} from '../validation';
import { RepoUrlPicker } from './RepoUrlPicker';

const ALLOWLIST_DOC_URL =
  'https://github.com/tomtom-internal/deployment-as-a-service/blob/main/docs/user/reference/allowed-repository-sources.md';

const REPO_TYPE_LABELS: Record<RepoType, string> = {
  git: 'Git',
  helm: 'Helm',
  oci: 'Helm (OCI)',
};

const REPO_TYPE_OPTIONS: RepoType[] = ['git', 'helm', 'oci'];

const DEPLOYMENT_TYPE_CONFIG = {
  manifests: {
    label: 'Manifests',
    description: 'Source for the Kubernetes manifests.',
    infoDoc: MANIFESTS_CONFIG_INFO,
    repoURLHelperText:
      'TomTom Git repository URL (tomtom-internal or tomtom-international GitHub orgs).',
    pathLabel: 'Manifests Path',
    pathHelperText: 'Repository path to the manifests directory.',
    pathErrorMessage:
      'Manifests Path must contain only letters, numbers, dots, hyphens, underscores, or slashes.',
  },
  helm: {
    label: 'Helm',
    description: 'Source for the Helm chart.',
    infoDoc: HELM_CONFIG_INFO,
    repoURLHelperText: (
      <>
        Source repository URL (Git, Helm, or OCI registry). See the{' '}
        <strong>
          <a href={ALLOWLIST_DOC_URL} target="_blank" rel="noreferrer">
            allowed URL prefixes
          </a>
        </strong>
        .
      </>
    ),
    pathLabel: 'Chart Path',
    pathHelperText: 'Repository path to the Helm chart directory.',
    pathErrorMessage:
      'Chart Path must contain only letters, numbers, dots, hyphens, underscores, or slashes.',
  },
  kustomize: {
    label: 'Kustomize',
    description: 'Source for the Kustomization.',
    infoDoc: KUSTOMIZE_CONFIG_INFO,
    repoURLHelperText:
      'TomTom Git repository URL (tomtom-internal or tomtom-international GitHub orgs).',
    pathLabel: 'Kustomization Path',
    pathHelperText: 'Repository path to the kustomization directory.',
    pathErrorMessage:
      'Kustomization Path must contain only letters, numbers, dots, hyphens, underscores, or slashes.',
  },
} as const;

export interface SourceFieldsProps {
  source: SourceConfiguration;
  onChange: (value: SourceConfiguration) => void;
  enableAppSetSupport: boolean;
  errors: string[];
}

export const SourceFields = ({
  source,
  onChange,
  enableAppSetSupport,
  errors,
}: SourceFieldsProps) => {
  const { classes } = useStyles();

  const inferred = inferRepoType(source.repoURL);
  const repoType = resolveRepoType(source);

  const typeConfig = source.deploymentType
    ? DEPLOYMENT_TYPE_CONFIG[source.deploymentType]
    : DEPLOYMENT_TYPE_CONFIG.helm;

  const writeURL = (url: string) => {
    onChange({ ...source, repoURL: url });
  };

  const handleFieldChange = <K extends keyof SourceConfiguration>(
    key: K,
    v: SourceConfiguration[K],
  ) => {
    onChange({ ...source, [key]: v });
  };

  const handleReleaseNameChange = (releaseName: string) => {
    const helm = source.helm ?? DEFAULT_HELM_CONFIGURATION;
    onChange({ ...source, helm: { ...helm, releaseName } });
  };

  let repoURLErrorNode: ReactNode = null;
  if (Boolean(errors?.length > 0)) {
    const isGitOnly =
      source.deploymentType === 'manifests' ||
      source.deploymentType === 'kustomize';
    if (
      isGitOnly &&
      !containsTemplateExpression(source.repoURL) &&
      !validateSourceGitURL(source.repoURL)
    ) {
      repoURLErrorNode = (
        <ScaffolderFieldError message="Source must be a valid TomTom repository from tomtom-internal or tomtom-international GitHub orgs." />
      );
    } else if (
      !isGitOnly &&
      !validateSourceRepoURL(source.repoURL, { enableAppSetSupport })
    ) {
      repoURLErrorNode = (
        <ScaffolderFieldError
          message={
            <>
              Source repository URL must match one of the{' '}
              <strong>
                <a href={ALLOWLIST_DOC_URL} target="_blank" rel="noreferrer">
                  allowed URL prefixes
                </a>
              </strong>
              .
            </>
          }
        />
      );
    }
  }

  return (
    <Box>
      <Box className={classes.inputContainer}>
        <ScaffolderInputHeader
          title={`${typeConfig.label} Configuration`}
          description={typeConfig.description}
          infoDoc={typeConfig.infoDoc}
        />
      </Box>

      <Box className={classes.inputContainer}>
        <RepoUrlPicker
          value={source.repoURL}
          onChange={writeURL}
          label="Repository URL"
          required
          error={
            Boolean(errors?.length > 0) &&
            (!validateSourceRepoURL(source.repoURL, { enableAppSetSupport }) ||
              ((source.deploymentType === 'manifests' ||
                source.deploymentType === 'kustomize') &&
                !containsTemplateExpression(source.repoURL) &&
                !validateSourceGitURL(source.repoURL)))
          }
          freeSolo
          helperText={repoURLErrorNode ?? typeConfig.repoURLHelperText}
        />
      </Box>

      {source.repoURL.trim() !== '' &&
        inferred === null &&
        source.deploymentType === 'helm' &&
        enableAppSetSupport && (
          <Box className={classes.inputContainer}>
            <Autocomplete
              size="small"
              disableClearable
              options={REPO_TYPE_OPTIONS}
              value={source.repoType ?? 'git'}
              getOptionLabel={option => REPO_TYPE_LABELS[option]}
              onChange={(_, option) => handleFieldChange('repoType', option)}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Repo Type"
                  required
                  variant="outlined"
                  margin="none"
                  fullWidth
                  helperText="Type of the templated repository URL (could not be inferred from the URL input)."
                />
              )}
            />
          </Box>
        )}

      {repoType === 'git' && (
        <>
          <Box className={classes.inputContainer}>
            <TextField
              label="Revision"
              required
              variant="outlined"
              margin="none"
              size="small"
              fullWidth
              value={source.targetRevision}
              onChange={e =>
                handleFieldChange('targetRevision', e.target.value)
              }
              error={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(source.targetRevision, {
                  enableAppSetSupport,
                })
              }
              helperText={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(source.targetRevision, {
                  enableAppSetSupport,
                }) ? (
                  <ScaffolderFieldError message="Revision must be a Git branch, commit hash, tag, or semver constraint." />
                ) : (
                  'The Git revision to deploy (a branch, tag, commit hash, or a semver constraint).'
                )
              }
            />
          </Box>
          <Box className={classes.inputContainer}>
            <TextField
              label={typeConfig.pathLabel}
              required
              variant="outlined"
              margin="none"
              size="small"
              fullWidth
              value={source.path ?? ''}
              onChange={e => handleFieldChange('path', e.target.value)}
              error={
                Boolean(errors?.length > 0) &&
                (!source.path ||
                  !validateSourceInputPattern(source.path, {
                    enableAppSetSupport,
                  }))
              }
              helperText={
                Boolean(errors?.length > 0) &&
                (!source.path ||
                  !validateSourceInputPattern(source.path, {
                    enableAppSetSupport,
                  })) ? (
                  <ScaffolderFieldError message={typeConfig.pathErrorMessage} />
                ) : (
                  typeConfig.pathHelperText
                )
              }
            />
          </Box>
        </>
      )}

      {repoType === 'helm' && (
        <>
          <Box className={classes.inputContainer}>
            <TextField
              label="Chart Name"
              required
              variant="outlined"
              margin="none"
              size="small"
              fullWidth
              value={source.chart ?? ''}
              onChange={e => handleFieldChange('chart', e.target.value)}
              error={
                Boolean(errors?.length > 0) &&
                (!source.chart ||
                  !validateSourceInputPattern(source.chart, {
                    enableAppSetSupport,
                  }))
              }
              helperText={
                Boolean(errors?.length > 0) &&
                (!source.chart ||
                  !validateSourceInputPattern(source.chart, {
                    enableAppSetSupport,
                  })) ? (
                  <ScaffolderFieldError message="Chart name must contain only letters, numbers, dots, hyphens, underscores, or slashes." />
                ) : (
                  'Name of the Helm chart in the registry (e.g. app or airflow).'
                )
              }
            />
          </Box>
          <Box className={classes.inputContainer}>
            <TextField
              label="Chart Version"
              required
              variant="outlined"
              margin="none"
              size="small"
              fullWidth
              value={source.targetRevision}
              onChange={e =>
                handleFieldChange('targetRevision', e.target.value)
              }
              error={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(source.targetRevision, {
                  enableAppSetSupport,
                })
              }
              helperText={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(source.targetRevision, {
                  enableAppSetSupport,
                }) ? (
                  <ScaffolderFieldError message="Chart version must be a valid version, git revision or semver constraint." />
                ) : (
                  'Helm chart version (e.g. 1.0.0), a semver constraint to track (e.g. 1.2.*), or git revision (if chart is stored in git).'
                )
              }
            />
          </Box>
        </>
      )}

      {repoType === 'oci' && (
        <Box className={classes.inputContainer}>
          <TextField
            label="Chart Version"
            required
            variant="outlined"
            margin="none"
            size="small"
            fullWidth
            value={source.targetRevision}
            onChange={e => handleFieldChange('targetRevision', e.target.value)}
            error={
              Boolean(errors?.length > 0) &&
              !validateSourceRevision(source.targetRevision, {
                enableAppSetSupport,
              })
            }
            helperText={
              Boolean(errors?.length > 0) &&
              !validateSourceRevision(source.targetRevision, {
                enableAppSetSupport,
              }) ? (
                <ScaffolderFieldError message="Chart version must be a valid version, git revision or semver constraint." />
              ) : (
                'Helm chart version (e.g. 1.0.0), or a semver constraint to track (e.g. 1.2.*).'
              )
            }
          />
        </Box>
      )}

      {source.deploymentType === 'helm' && (
        <Box className={classes.inputContainer}>
          <TextField
            label="Release Name"
            required
            variant="outlined"
            margin="none"
            size="small"
            fullWidth
            value={source.helm?.releaseName ?? ''}
            onChange={e => handleReleaseNameChange(e.target.value)}
            error={
              Boolean(errors?.length > 0) &&
              (!source.helm?.releaseName ||
                !validateHelmReleaseName(source.helm.releaseName, {
                  enableAppSetSupport,
                }))
            }
            helperText={
              Boolean(errors?.length > 0) &&
              (!source.helm?.releaseName ||
                !validateHelmReleaseName(source.helm.releaseName, {
                  enableAppSetSupport,
                })) ? (
                <ScaffolderFieldError message="Release name must be lowercase alphanumeric and hyphens (max 53 characters)." />
              ) : (
                'Helm release name. Lowercase alphanumeric and hyphens, max 53 characters.'
              )
            }
          />
        </Box>
      )}
    </Box>
  );
};
